import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import yaml from 'js-yaml';

const BASE_URL = process.env.REACT_APP_BACKEND_URL || '/api';

interface SpecSummary {
  id: number;
  name: string;
  title: string;
}

interface EndpointExample {
  path: string;
  method: string;
  examples: string[];
  description?: string;
}

interface UseSpecsReturn {
  specs: SpecSummary[];
  endpoints: EndpointExample[];
  loadingSpecs: boolean;
  loadingEndpoints: boolean;
  loadEndpoints: (specId: number) => Promise<void>;
}

const HTTP_METHODS = new Set(['get', 'post', 'put', 'delete', 'patch', 'options', 'head', 'trace']);

export function useSpecs(): UseSpecsReturn {
  const [specs, setSpecs] = useState<SpecSummary[]>([]);
  const [endpoints, setEndpoints] = useState<EndpointExample[]>([]);
  const [loadingSpecs, setLoadingSpecs] = useState(false);
  const [loadingEndpoints, setLoadingEndpoints] = useState(false);

  useEffect(() => {
    setLoadingSpecs(true);
    axios
      .get(`${BASE_URL}/specs`)
      .then(({ data }) => setSpecs(data))
      .catch(() => setSpecs([]))
      .finally(() => setLoadingSpecs(false));
  }, []);

  const loadEndpoints = useCallback(async (specId: number) => {
    setLoadingEndpoints(true);
    setEndpoints([]);
    try {
      const { data } = await axios.get(`${BASE_URL}/specs/${specId}`);
      const endpointMap = new Map<string, EndpointExample>();

      const addEndpoint = (path: string, method: string, example?: string, description?: string) => {
        const normalizedMethod = method.toLowerCase();
        const key = `${normalizedMethod}:${path}`;
        if (!endpointMap.has(key)) {
          endpointMap.set(key, {
            path,
            method: normalizedMethod,
            examples: [],
            description,
          });
        } else if (description) {
          const existing = endpointMap.get(key)!;
          if (!existing.description) {
            existing.description = description;
          }
        }
        if (example) {
          const entry = endpointMap.get(key)!;
          if (!entry.examples.includes(example)) {
            entry.examples.push(example);
          }
        }
      };

      // Extract endpoints from spec content (all endpoints)
      const content = data?.content || '';
      if (content) {
        try {
          const parsed = yaml.load(content) as any;
          const paths = parsed?.paths || {};
          if (paths && typeof paths === 'object') {
            for (const [path, pathItem] of Object.entries(paths)) {
              if (!pathItem || typeof pathItem !== 'object') continue;
              for (const [method, op] of Object.entries(pathItem as Record<string, any>)) {
                const normalized = String(method).toLowerCase();
                if (HTTP_METHODS.has(normalized)) {
                  const description = op?.summary || op?.description || '';
                  addEndpoint(path, normalized, undefined, description);
                }
              }
            }
          }
        } catch {
          // ignore parse errors
        }
      }

      // Merge endpoint settings (examples)
      const settings = data?.endpointSettings || [];
      for (const setting of settings) {
        if (!setting?.path || !setting?.method) continue;
        addEndpoint(setting.path, setting.method, setting.selectedExample);
      }

      const sorted = Array.from(endpointMap.values()).sort((a, b) => {
        if (a.path === b.path) {
          return a.method.localeCompare(b.method);
        }
        return a.path.localeCompare(b.path);
      });

      setEndpoints(sorted);
    } catch {
      setEndpoints([]);
    } finally {
      setLoadingEndpoints(false);
    }
  }, []);

  return { specs, endpoints, loadingSpecs, loadingEndpoints, loadEndpoints };
}
